import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft,
  Shield,
  Eye,
  Database,
  Users,
  Lock,
  FileText,
  Mail,
  CheckCircle
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

const PrivacidadPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white/60">
      <PageHeader hideCTA={true} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Shield className="text-blue-600" size={32} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('privacidad.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('privacidad.subtitle')}
            </p>
          </div>

          {/* Información de la Política */}
          <div className="bg-gray-50 rounded-lg p-6 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-[#141414]">{t('privacidad.statement_title')}</h2>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <span className="font-medium">{t('privacidad.review_date')}:</span> 02/01/2023 | 
              <span className="font-medium ml-2">{t('privacidad.approval_date')}:</span> 02/01/2023
            </div>
          </div>

          {/* Descripción Principal */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {t('privacidad.main_desc')}
            </p>
          </div>

          {/* Políticas de Privacidad */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#141414] mb-8 text-center">{t('privacidad.policies_title')}</h2>
            
            <div className="grid gap-6">
              
              {/* Información que Recopilamos */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Database className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.collection.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.collection.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Uso de la Información */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Eye className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.usage.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.usage.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compartir Información */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.sharing.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.sharing.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seguridad */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Lock className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.security.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.security.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sus Derechos */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="text-indigo-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.rights.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.rights.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cambios en esta Declaración */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.changes.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.changes.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cómo Contactarnos */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('privacidad.items.contact.title')}</h3>
                    <p className="text-gray-600">
                      {t('privacidad.items.contact.desc')} <a href="mailto:hello@nextcoders.work" className="text-blue-600 hover:underline">hello@nextcoders.work</a>.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Aceptación */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="text-blue-600" size={32} />
            </div>
            <p className="text-blue-800 font-medium">
              {t('privacidad.acceptance')}
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PrivacidadPage;
